import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { wsManager } from '@/api/client';
import type { WebSocketMessage, MonitoringResult, CalibrationInfo } from '@/types';

type ConnectionStatus = 'disconnected' | 'connecting' | 'connected' | 'reconnecting' | 'failed';

interface QueuedMessage {
  id: string;
  data: any;
  timestamp: number;
  retries: number;
}

interface WebSocketState {
  status: ConnectionStatus;
  sessionId: string | null;
  isConnected: boolean;
  lastConnected: number | null;
  reconnectAttempts: number;
  messageQueue: QueuedMessage[];
  listeners: Map<string, Set<(data: any) => void>>;
  error: string | null;
  lastError: number | null;
}

interface WebSocketActions {
  connect: (sessionId: string) => Promise<void>;
  disconnect: () => void;
  send: (data: any) => void;
  sendMessage: (data: any) => void;
  queueMessage: (data: any) => void;
  processQueue: () => void;
  clearQueue: () => void;
  subscribe: (event: string, callback: (data: any) => void) => () => void;
  setStatus: (status: ConnectionStatus) => void;
  setError: (error: string | null) => void;
  resetReconnectAttempts: () => void;
  clearError: () => void;
  _handleOpen: () => void;
  _handleClose: (data: { code: number; reason: string }) => void;
  _handleError: (error: any) => void;
  _handleMessage: (message: WebSocketMessage) => void;
}

type WebSocketStore = WebSocketState & WebSocketActions;

export const useWebSocketStore = create<WebSocketStore>()(
  devtools(
    (set, get) => ({
      // Initial state
      status: 'disconnected',
      sessionId: null,
      isConnected: false,
      lastConnected: null,
      reconnectAttempts: 0,
      messageQueue: [],
      listeners: new Map(),
      error: null,
      lastError: null,

      // Actions
      connect: async (sessionId: string) => {
        const currentState = get();
        
        if (currentState.status === 'connecting' && currentState.sessionId === sessionId) {
          console.log('📋 Already connecting to this session');
          return;
        }

        if (currentState.status === 'connected' && currentState.sessionId === sessionId) {
          console.log('📋 Already connected to this session');
          return;
        }

        set({ 
          status: 'connecting', 
          sessionId, 
          error: null,
          lastError: null,
          reconnectAttempts: currentState.status === 'reconnecting' ? currentState.reconnectAttempts + 1 : 0
        });

        try {
          // Clear any existing listeners to prevent duplicates
          wsManager.off('open', get()._handleOpen);
          wsManager.off('close', get()._handleClose);
          wsManager.off('error', get()._handleError);
          wsManager.off('message', get()._handleMessage);

          // Setup WebSocket event handlers
          wsManager.on('open', get()._handleOpen);
          wsManager.on('close', get()._handleClose);
          wsManager.on('error', get()._handleError);
          wsManager.on('message', get()._handleMessage);

          // Connect using the WebSocket manager
          await wsManager.connect(sessionId);
          
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Connection failed';
          console.error('❌ WebSocket connection failed:', errorMessage);
          set({
            status: 'failed',
            isConnected: false,
            error: errorMessage,
            lastError: Date.now(),
          });
          throw error;
        }
      },

      disconnect: () => {
        console.log('🔌 Disconnecting WebSocket...');
        set({
          status: 'disconnected',
          sessionId: null,
          isConnected: false,
          error: null,
          lastError: null,
        });
        
        wsManager.disconnect();
        get().clearQueue();
        get().resetReconnectAttempts();
      },

      send: (data: any) => {
        const { isConnected } = get();
        
        if (isConnected && wsManager.isConnected) {
          wsManager.send(data);
        } else {
          console.warn('⚠️ WebSocket not connected, queueing message:', data.type);
          get().queueMessage(data);
        }
      },

      sendMessage: (data: any) => {
        // Alias for send method
        get().send(data);
      },

      queueMessage: (data: any) => {
        const message: QueuedMessage = {
          id: `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          data,
          timestamp: Date.now(),
          retries: 0,
        };
        
        set(state => ({
          messageQueue: [...state.messageQueue, message],
        }));
      },

      processQueue: () => {
        const { messageQueue, isConnected } = get();
        
        if (!isConnected || messageQueue.length === 0) {
          return;
        }

        console.log(`📤 Processing ${messageQueue.length} queued messages`);

        // Process messages in order
        const validMessages = messageQueue.filter(msg => {
          // Remove messages older than 5 minutes or with too many retries
          const isExpired = Date.now() - msg.timestamp > 5 * 60 * 1000;
          const tooManyRetries = msg.retries >= 3;
          
          return !isExpired && !tooManyRetries;
        });

        validMessages.forEach(message => {
          try {
            wsManager.send(message.data);
          } catch (error) {
            console.error('❌ Failed to send queued message:', error);
            message.retries++;
          }
        });

        // Clear processed messages
        set({ messageQueue: [] });
      },

      clearQueue: () => {
        set({ messageQueue: [] });
      },

      subscribe: (event: string, callback: (data: any) => void) => {
        const { listeners } = get();
        
        if (!listeners.has(event)) {
          listeners.set(event, new Set());
        }
        listeners.get(event)!.add(callback);
        
        set({ listeners: new Map(listeners) });

        // Return unsubscribe function
        return () => {
          const currentListeners = get().listeners;
          const eventListeners = currentListeners.get(event);
          if (eventListeners) {
            eventListeners.delete(callback);
            if (eventListeners.size === 0) {
              currentListeners.delete(event);
            }
          }
          set({ listeners: new Map(currentListeners) });
        };
      },

      setStatus: (status: ConnectionStatus) => {
        set({ status, isConnected: status === 'connected' });
      },

      setError: (error: string | null) => {
        set({ error, lastError: error ? Date.now() : null });
      },

      clearError: () => {
        set({ error: null, lastError: null });
      },

      resetReconnectAttempts: () => {
        set({ reconnectAttempts: 0 });
      },

      // Internal event handlers
      _handleOpen: () => {
        console.log('🟢 WebSocket connected successfully');
        set({
          status: 'connected',
          isConnected: true,
          lastConnected: Date.now(),
          error: null,
          lastError: null,
        });
        
        // Process any queued messages
        get().processQueue();
        get().resetReconnectAttempts();
      },

      _handleClose: (data: { code: number; reason: string }) => {
        console.log(`📴 WebSocket closed: ${data.code} ${data.reason}`);
        
        const currentState = get();
        
        // Only attempt reconnection if we have a session and aren't at max attempts
        if (currentState.sessionId && currentState.reconnectAttempts < 5) {
          set({ status: 'reconnecting' });
          
          // Exponential backoff reconnection
          const delay = Math.min(1000 * Math.pow(2, currentState.reconnectAttempts), 30000);
          console.log(`🔄 Reconnecting in ${delay}ms (attempt ${currentState.reconnectAttempts + 1}/5)`);
          
          setTimeout(() => {
            const state = get();
            if (state.sessionId && state.status === 'reconnecting') {
              get().connect(state.sessionId).catch(error => {
                console.error('❌ Reconnection attempt failed:', error);
              });
            }
          }, delay);
        } else {
          set({
            status: 'disconnected',
            isConnected: false,
          });
        }
      },

      _handleError: (error: any) => {
        const errorMessage = error?.message || 'WebSocket connection failed';
        console.error('❌ WebSocket error:', errorMessage);
        
        set({
          status: 'failed',
          isConnected: false,
          error: errorMessage,
          lastError: Date.now(),
        });
      },

      _handleMessage: (message: WebSocketMessage) => {
        // Log monitoring results for debugging (throttled)
        if (message.type === 'monitoring_result') {
          console.debug('📊 Received monitoring data:', message.data?.system_used);
        }
        
        // Forward message to subscribers
        const listeners = get().listeners.get(message.type);
        if (listeners) {
          const wireMessage = message as WebSocketMessage & { message?: string };
          const payload =
            message.data ??
            (typeof wireMessage.message === 'string'
              ? { message: wireMessage.message }
              : message);
          listeners.forEach(callback => {
            try {
              callback(payload);
            } catch (error) {
              console.error(`❌ Error in ${message.type} callback:`, error);
            }
          });
        }
        
        // Also forward to generic message listeners
        const messageListeners = get().listeners.get('message');
        if (messageListeners) {
          messageListeners.forEach(callback => {
            try {
              callback(message);
            } catch (error) {
              console.error('❌ Error in message callback:', error);
            }
          });
        }
      },
    }),
    { name: 'WebSocketStore' }
  )
);

// Connection health monitoring
let healthCheckInterval: NodeJS.Timeout | null = null;

export const startConnectionHealthCheck = () => {
  if (healthCheckInterval) {
    clearInterval(healthCheckInterval);
  }

  healthCheckInterval = setInterval(() => {
    const { isConnected, sessionId, connect, status } = useWebSocketStore.getState();
    
    // Only attempt reconnection if we're not already connecting/reconnecting
    if (!isConnected && sessionId && status !== 'connecting' && status !== 'reconnecting') {
      console.log('🏥 Health check: attempting reconnection');
      connect(sessionId).catch(error => {
        console.error('❌ Health check reconnection failed:', error);
      });
    }
  }, 30000); // Check every 30 seconds
};

export const stopConnectionHealthCheck = () => {
  if (healthCheckInterval) {
    clearInterval(healthCheckInterval);
    healthCheckInterval = null;
  }
};

// Convenience hooks for specific event types
export const useMonitoringData = (callback: (data: MonitoringResult) => void) => {
  const subscribe = useWebSocketStore(state => state.subscribe);
  
  return subscribe('monitoring_result', callback);
};

export const useCalibrationUpdates = (callback: (data: CalibrationInfo) => void) => {
  const subscribe = useWebSocketStore(state => state.subscribe);
  
  return subscribe('calibration_update', callback);
};

export const useSessionUpdates = (callback: (data: any) => void) => {
  const subscribe = useWebSocketStore(state => state.subscribe);
  
  return subscribe('session_update', callback);
};

export const useExamUpdates = (callback: (data: any) => void) => {
  const subscribe = useWebSocketStore(state => state.subscribe);
  
  return subscribe('exam_update', callback);
};